import {
  collection,
  doc,
  getDoc,
  setDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';
import { authService } from './authService';
import { functions } from '../config/firebase';
import {
  callablePatientQaGetThread,
  callablePatientQaEnsureThread,
  callablePatientQaSendMessage,
  callablePatientQaListMessages,
} from '../utils/patientConsentCallable';

const THREADS_COLLECTION = 'consentQaThreads';

function threadIdFor(consentCollection, consentDocId) {
  return `${consentCollection}_${consentDocId}`;
}

function safeString(v) {
  return v != null ? String(v) : '';
}

export const consentQaService = {
  threadIdFor,

  async getThreadByConsent(consentCollection, consentDocId) {
    const id = threadIdFor(consentCollection, consentDocId);
    const snap = await getDoc(doc(db, THREADS_COLLECTION, id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  },

  async getThreadByAccessToken(accessToken) {
    const token = safeString(accessToken).trim();
    if (!token) return null;
    return callablePatientQaGetThread(token);
  },

  async ensureThread({
    consentCollection,
    consentDocId,
    consentId,
    clinicId,
    clinicName,
    accessToken,
    patientName,
    patientEmail,
    treatmentLabel,
  }) {
    if (accessToken) {
      return callablePatientQaEnsureThread(accessToken, {
        consentCollection,
        consentDocId,
        consentId,
        clinicId,
        clinicName,
        patientName,
        patientEmail,
        treatmentLabel,
      });
    }

    const id = threadIdFor(consentCollection, consentDocId);
    const ref = doc(db, THREADS_COLLECTION, id);
    const existing = await getDoc(ref);
    if (existing.exists()) return { id: existing.id, ...existing.data() };

    const payload = {
      consentCollection: safeString(consentCollection),
      consentDocId: safeString(consentDocId),
      consentId: safeString(consentId || consentDocId),
      clinicId: safeString(clinicId),
      clinicName: safeString(clinicName),
      accessToken: safeString(accessToken),
      patientName: safeString(patientName),
      patientEmail: safeString(patientEmail),
      treatmentLabel: safeString(treatmentLabel),
      status: 'open', // open | answered | closed
      unreadByClinic: 0,
      unreadByPatient: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      lastMessageAt: serverTimestamp(),
      lastMessagePreview: '',
    };

    await setDoc(ref, payload);
    return { id, ...payload };
  },

  async listClinicThreads(clinicId, { status = 'all', pageSize = 30 } = {}) {
    const conditions = [where('clinicId', '==', safeString(clinicId))];
    if (status && status !== 'all') conditions.push(where('status', '==', status));
    // Prefer ordered query (best UX), but gracefully fall back if Firestore needs a composite index.
    try {
      const q = query(
        collection(db, THREADS_COLLECTION),
        ...conditions,
        orderBy('lastMessageAt', 'desc'),
        limit(pageSize)
      );
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) {
      const msg = String(e?.message || '');
      if (!msg.toLowerCase().includes('requires an index')) throw e;
      const q2 = query(collection(db, THREADS_COLLECTION), ...conditions, limit(pageSize));
      const snap2 = await getDocs(q2);
      const items = snap2.docs.map((d) => ({ id: d.id, ...d.data() }));
      const toMs = (ts) => {
        try {
          if (!ts) return 0;
          if (typeof ts.toDate === 'function') return ts.toDate().getTime();
          if (typeof ts.seconds === 'number') return ts.seconds * 1000;
          const d = new Date(ts);
          return isNaN(d.getTime()) ? 0 : d.getTime();
        } catch {
          return 0;
        }
      };
      items.sort((a, b) => toMs(b.lastMessageAt) - toMs(a.lastMessageAt));
      return items;
    }
  },

  async listMessages(threadId, { pageSize = 80, accessToken = null } = {}) {
    if (accessToken) {
      return callablePatientQaListMessages(accessToken);
    }
    const q = query(
      collection(db, THREADS_COLLECTION, threadId, 'messages'),
      orderBy('createdAt', 'asc'),
      limit(pageSize)
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  },

  async sendPatientMessage({
    thread,
    text,
    meta = {},
    accessToken: accessTokenOverride = null,
  }) {
    const body = safeString(text).trim();
    if (!body) throw new Error('Message cannot be empty.');

    const token = safeString(thread?.accessToken || accessTokenOverride).trim();
    if (token) {
      return callablePatientQaSendMessage(token, body, meta);
    }

    let ipAddress = 'unknown';
    try {
      ipAddress = await authService.getClientIP();
    } catch (_) {}

    const msg = {
      senderType: 'patient',
      text: body,
      createdAt: serverTimestamp(),
      meta: {
        ...meta,
        ipAddress,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      },
    };

    await addDoc(collection(db, THREADS_COLLECTION, thread.id, 'messages'), msg);
    await updateDoc(doc(db, THREADS_COLLECTION, thread.id), {
      status: 'open',
      unreadByClinic: (thread.unreadByClinic || 0) + 1,
      lastMessageAt: serverTimestamp(),
      lastMessagePreview: body.slice(0, 140),
      updatedAt: serverTimestamp(),
    });

    // Keep the parent consent doc status in sync so clinic "Consents" list shows "Has Questions".
    // This is best-effort and should never block messaging UX.
    try {
      const coll = safeString(thread.consentCollection);
      const consentDocId = safeString(thread.consentDocId);
      if (coll && consentDocId) {
        if (coll === 'aiConsents') {
          await updateDoc(doc(db, 'aiConsents', consentDocId), {
            status: 'has_questions',
            patientQuestions: body,
            questionsSubmittedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        } else if (coll === 'consents') {
          await updateDoc(doc(db, 'consents', consentDocId), {
            status: 'questions_requested',
            questionText: body,
            questionsRequestedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }
      }
    } catch (e) {
      console.warn('[consentQaService] Could not sync consent status:', e?.message || e);
    }
  },

  async sendClinicReply({
    thread,
    text,
    staffUser,
  }) {
    const body = safeString(text).trim();
    if (!body) throw new Error('Reply cannot be empty.');

    const msg = {
      senderType: 'clinic',
      text: body,
      createdAt: serverTimestamp(),
      meta: {
        staffUserId: staffUser?.uid || staffUser?.userId || null,
        staffEmail: staffUser?.email || null,
        staffName: staffUser?.displayName || staffUser?.name || null,
        staffRole: staffUser?.role || null,
      },
    };

    await addDoc(collection(db, THREADS_COLLECTION, thread.id, 'messages'), msg);
    await updateDoc(doc(db, THREADS_COLLECTION, thread.id), {
      status: 'answered',
      unreadByPatient: (thread.unreadByPatient || 0) + 1,
      lastMessageAt: serverTimestamp(),
      lastMessagePreview: body.slice(0, 140),
      updatedAt: serverTimestamp(),
      unreadByClinic: 0,
    });

    // So clinic list no longer shows "Has Questions" after a reply (patient may follow up via thread).
    try {
      const coll = safeString(thread.consentCollection);
      const consentDocId = safeString(thread.consentDocId);
      if (coll && consentDocId) {
        if (coll === 'aiConsents') {
          await updateDoc(doc(db, 'aiConsents', consentDocId), {
            status: 'questions_answered',
            clinicRepliedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        } else if (coll === 'consents') {
          await updateDoc(doc(db, 'consents', consentDocId), {
            status: 'questions_answered',
            clinicRepliedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }
      }
    } catch (e) {
      console.warn('[consentQaService] Could not sync consent status after clinic reply:', e?.message || e);
    }

    // Email notify patient (best-effort). Patient can click through to see the reply thread.
    try {
      const toEmail = safeString(thread.patientEmail).trim();
      if (toEmail) {
        const origin =
          typeof window !== 'undefined' && window.location?.origin
            ? window.location.origin
            : 'https://smileconsent.co.uk';
        const token = safeString(thread.accessToken).trim();
        const isAi = safeString(thread.consentCollection) === 'aiConsents';
        const linkParams = new URLSearchParams({ view: 'messages', focus: 'chat' });
        const linkUrl = token
          ? `${origin}/${isAi ? 'sign-consent' : 'consent'}/${encodeURIComponent(token)}?${linkParams.toString()}`
          : origin;

        const sendQaReplyNotification = httpsCallable(functions, 'sendQaReplyNotification');
        await sendQaReplyNotification({
          toEmail,
          patientName: safeString(thread.patientName) || 'Patient',
          clinicId: safeString(thread.clinicId),
          clinicName: safeString(thread.clinicName) || 'SmileConsent clinic',
          replyPreview: body.slice(0, 220),
          linkUrl,
        });
      }
    } catch (e) {
      console.warn('[consentQaService] patient reply email failed:', e?.message || e);
    }
  },

  async markClinicRead(threadId) {
    await updateDoc(doc(db, THREADS_COLLECTION, threadId), {
      unreadByClinic: 0,
      updatedAt: serverTimestamp(),
    });
  },

  async markPatientRead(threadId) {
    await updateDoc(doc(db, THREADS_COLLECTION, threadId), {
      unreadByPatient: 0,
      updatedAt: serverTimestamp(),
    });
  },
};

